import { useAuthenticator } from '@aws-amplify/ui-react'
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { AttachmentData, MessageUser } from '../Messages/types'
import { useUser } from '../User/User.provider'

type WebSocketMessage = {
  type: 'message' | 'notification'
  data: any
}

export type sqsMessage = {
  type: string
  threadId: number
  messageId: number
  messageBody: string
  fromUserId: number
  fromUser: MessageUser
  toUsers: MessageUser[]
  receivedDateTime: string
  attachments: AttachmentData[]
}

export interface WebSocketContextInterface {
  isConnected: boolean
  sqsMessages: sqsMessage[]
  sendWebSocketMessage: (message: any) => void
}

export const WebSocketContext = createContext<WebSocketContextInterface>({
  isConnected: false,
  sqsMessages: [],
  sendWebSocketMessage: function (message: any): void {
    throw new Error('Function not implemented.')
  },
})

type WebSocketProviderProps = {
  children: React.ReactNode
  url: string
}

export const WebSocketProvider = ({
  children,
  url,
}: WebSocketProviderProps) => {
  const ws = useRef<WebSocket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [sqsMessages, setSqsMessages] = useState<sqsMessage[]>([])

  const { authStatus } = useAuthenticator()
  const { token } = useUser()

  const uniqueMessageIds = useRef<Set<number>>(new Set())
  const reconnectTimeout = useRef<NodeJS.Timeout>()
  const pingInterval = useRef<NodeJS.Timeout>()
  const maxReconnectAttempts = 25
  const reconnectAttempts = useRef(0)

  const addMessage = useCallback((message: sqsMessage) => {
    if (!uniqueMessageIds.current.has(message.messageId)) {
      uniqueMessageIds.current.add(message.messageId)
      setSqsMessages((prevMessages) => [...prevMessages, message])
    }
  }, [])

  const sendWebSocketMessage = useCallback((message: any) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(message)
    } else {
      console.error('WebSocket is not connected')
    }
  }, [])

  const disconnect = useCallback(() => {
    if (reconnectTimeout.current) {
      clearTimeout(reconnectTimeout.current)
      reconnectTimeout.current = undefined
    }
    if (pingInterval.current) {
      clearInterval(pingInterval.current)
      pingInterval.current = undefined
    }

    if (ws.current) {
      ws.current.close()
      ws.current = null
    }

    setIsConnected(false)
  }, [])

  const connect = useCallback(() => {
    if (
      ws.current?.readyState === WebSocket.OPEN ||
      ws.current?.readyState === WebSocket.CONNECTING
    ) {
      return
    }

    if (authStatus !== 'authenticated' || !token) {
      return
    }

    try {
      ws.current = new WebSocket(url, ['jwt', token])

      ws.current.onopen = () => {
        console.log('WebSocket Connected')
        setIsConnected(true)
        reconnectAttempts.current = 0

        ws.current?.send(
          JSON.stringify({
            action: 'subscribe',
            data: { messageType: 'userMessages' },
          })
        )

        if (pingInterval.current) {
          clearInterval(pingInterval.current)
        }
        pingInterval.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send('ping')
          }
        }, 15000)
      }

      ws.current.onclose = () => {
        console.log('WebSocket Disconnected')
        setIsConnected(false)

        if (pingInterval.current) {
          clearInterval(pingInterval.current)
          pingInterval.current = undefined
        }

        if (
          reconnectAttempts.current < maxReconnectAttempts &&
          authStatus === 'authenticated'
        ) {
          const delay = Math.min(
            3000 * Math.pow(2, reconnectAttempts.current),
            30000
          )
          reconnectTimeout.current = setTimeout(() => {
            reconnectAttempts.current += 1
            connect()
          }, delay)
        }
      }

      ws.current.onerror = (error) => {
        console.error('WebSocket Error:', error)
      }

      ws.current.onmessage = (event) => {
        try {
          if (event.data === 'pong') {
            return
          }
          const messageFirstParse: WebSocketMessage = JSON.parse(event.data)
          const messageFullyParse = JSON.parse(messageFirstParse.data)

          if (messageFirstParse.type === 'message') {
            addMessage(messageFullyParse.data)
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }
    } catch (error) {
      console.error('Error creating WebSocket connection:', error)
    }
  }, [authStatus, token, url, addMessage])

  useEffect(() => {
    if (authStatus === 'authenticated' && token) {
      connect()
    } else {
      disconnect()
    }

    return () => {
      disconnect()
    }
  }, [authStatus, token])

  const contextValue = useMemo(
    () => ({
      isConnected,
      sqsMessages,
      sendWebSocketMessage,
    }),
    [isConnected, sqsMessages, sendWebSocketMessage]
  )

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  )
}
